import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { createLogger } from '@/core/logger'
import { TOKENS, TOKEN_DEFAULTS } from './tokens'
import { parseCssColor, shiftDelta, applyDelta, formatOklch, contrastRatio } from './color-math'
import { THEME_PRESETS, type ThemePreset } from './presets'

const logger = createLogger('ThemeLabStore')

interface ThemeLabState {
  /** Panel visibility — transient, not persisted. */
  open: boolean
  /** CSS var name → override value. Persisted to localStorage so the browser
   * keeps the look across reloads. Cleared with `resetAll`. */
  overrides: Record<string, string>
  /** Which preset is currently active. null = custom / anchor-shift result. */
  activePresetId: ThemePreset['id'] | null

  setOpen: (open: boolean) => void
  toggle: () => void
  setToken: (name: string, value: string) => void
  /** Revert a single token back to its baseline default (clear the override). */
  revertToken: (name: string) => void
  resetAll: () => void
  /** Apply a bundled preset — replaces current overrides. Empty overrides
   * map (night-ops) is equivalent to full reset. */
  applyPreset: (id: ThemePreset['id']) => void
  /**
   * Compute the OKLCH delta between the baseline `--accent` and the given
   * target anchor color, then shift every color token by that delta and store
   * the results. Sets `activePresetId` to null (custom). Clamps results to
   * legal OKLCH ranges and ensures fg/bg contrast stays ≥ 4.5:1.
   */
  applyAnchorShift: (targetAccentColor: string) => void
}

export const useThemeLabStore = create<ThemeLabState>()(
  persist(
    (set, _get) => ({
      open: false,
      overrides: {},
      activePresetId: 'night-ops',

      setOpen: (open) => set({ open }),
      toggle: () => set((s) => ({ open: !s.open })),

      setToken: (name, value) =>
        set((s) => ({
          overrides: { ...s.overrides, [name]: value },
          activePresetId: null,
        })),

      revertToken: (name) =>
        set((s) => {
          if (!(name in s.overrides)) return s
          const next = { ...s.overrides }
          delete next[name]
          return { overrides: next }
        }),

      resetAll: () => set({ overrides: {}, activePresetId: 'night-ops' }),

      applyPreset: (id) => {
        const preset = THEME_PRESETS.find((p) => p.id === id)
        if (!preset) {
          logger.w('applyPreset: unknown preset id', id)
          return
        }
        set({ overrides: { ...preset.overrides }, activePresetId: id })
      },

      applyAnchorShift: (targetAccentColor) => {
        // 1. Parse the target anchor color
        const targetOklch = parseCssColor(targetAccentColor)
        if (!targetOklch) {
          logger.w('applyAnchorShift: cannot parse anchor color', targetAccentColor)
          return
        }

        // 2. Parse the *baseline* accent (from TOKEN_DEFAULTS — stable, not
        //    affected by current overrides on documentElement).
        const baseAccentStr = TOKEN_DEFAULTS['--accent'] ?? '#4fc3f7'
        const baseAccentOklch = parseCssColor(baseAccentStr)
        if (!baseAccentOklch) {
          logger.w('applyAnchorShift: cannot parse baseline accent', baseAccentStr)
          return
        }

        // 3. Compute delta from baseline accent to the target
        const delta = shiftDelta(baseAccentOklch, targetOklch)

        // 4. Iterate every color token and apply the delta
        const colorTokens = TOKENS.filter((t) => t.kind === 'color')
        const newOverrides: Record<string, string> = {}

        for (const token of colorTokens) {
          const baseStr = TOKEN_DEFAULTS[token.name] ?? ''
          const baseOklch = parseCssColor(baseStr)
          if (!baseOklch) {
            // Token uses a non-color value (e.g., 'transparent') — skip
            continue
          }
          const shifted = applyDelta(baseOklch, delta)
          newOverrides[token.name] = formatOklch(shifted)
        }

        // 5. Contrast guard: check fg vs bg
        const fgStr = newOverrides['--fg'] ?? TOKEN_DEFAULTS['--fg'] ?? '#eaf2fb'
        const bgStr = newOverrides['--bg-deep'] ?? TOKEN_DEFAULTS['--bg-deep'] ?? '#000a16'
        const fgOklch = parseCssColor(fgStr)
        const bgOklch = parseCssColor(bgStr)

        if (fgOklch && bgOklch) {
          const ratio = contrastRatio(fgOklch, bgOklch)
          if (ratio < 4.5) {
            logger.w(
              `applyAnchorShift: fg/bg contrast ${ratio.toFixed(2)} < 4.5 (AA). Clamping fg lightness.`,
            )
            // Brighten fg until contrast passes. Step up by 5% increments.
            let fixedFg = { ...fgOklch }
            for (let attempt = 0; attempt < 20; attempt++) {
              fixedFg = { ...fixedFg, l: Math.min(1, fixedFg.l + 0.05) }
              if (contrastRatio(fixedFg, bgOklch) >= 4.5) break
            }
            newOverrides['--fg'] = formatOklch(fixedFg)
          }
        }

        // 6. Persist, clear activePresetId (custom result)
        set({ overrides: newOverrides, activePresetId: null })
      },
    }),
    {
      name: 'cm.themeLab',
      storage: createJSONStorage(() => localStorage),
      // `open` is transient UI state — only persist overrides + activePresetId.
      partialize: (state) => ({
        overrides: state.overrides,
        activePresetId: state.activePresetId,
      }),
    },
  ),
)
